import { WEAPON_ICONS } from '../data/assets/visualAssets';
import { EventBus } from '../core/EventBus';
import { PlayerStats } from '../components/PlayerStats';
import { ActiveWeaponSnapshot, WeaponSystem } from './WeaponSystem';
import { LocalizationManager } from '../managers/LocalizationManager';
import { createLogger } from '../utils/Logger';
import { ContentRegistry, loadContentRegistry, PassiveContent, PassiveModifier } from '../data';
import {
    createDebugCommandFailure,
    createDebugCommandSuccess,
    DebugCommandResult,
    DebugInventorySnapshot,
    GiveWeaponDebugPayload,
    GiveWeaponDebugResult,
    resolveDebugCommandSource
} from './DebugCommandTypes';

export type PassiveId = string;

export interface ActivePassive {
    id: PassiveId;
    level: number;
}

export interface ActivePassiveSnapshot extends ActivePassive {
    nameKey: string;
    icon: string;
    color: number;
    maxLevel: number;
}

export type UpgradeOptionType =
    | 'weapon_unlock'
    | 'weapon_level'
    | 'passive_unlock'
    | 'passive_level'
    | 'consumable';

export interface UpgradeOption {
    id: string;
    type: UpgradeOptionType;
    title: string;
    description: string;
    icon?: string;
    color?: number;
    weaponId?: string;
    passiveId?: PassiveId;
    consumableId?: string;
    value?: number;
}

export interface UpgradeInventorySnapshot {
    weapons: ActiveWeaponSnapshot[];
    passives: ActivePassiveSnapshot[];
    maxWeapons: number;
    maxPassives: number;
}

export interface EvolutionRecipe {
    passiveRequired: PassiveId;
    passiveLevelRequired: number;
    evolvedWeaponId: string;
}

export interface EvolutionRewardPayload {
    baseWeapon: string;
    evolvedWeapon: string;
    baseWeaponId: string;
    evolvedWeaponId: string;
    name: string;
    icon: string;
    color?: number;
    nameKey?: string;
    descriptionKey?: string;
}

export interface EvolutionApplyResult {
    success: boolean;
    reason?: string;
}

export type ChestRewardResult =
    | { mode: 'evolution'; evolution: EvolutionRewardPayload }
    | { mode: 'single-upgrade'; upgrades: UpgradeOption[] }
    | { mode: 'multi-upgrade'; upgrades: UpgradeOption[] };

type RandomSource = () => number;

export interface UpgradeSystemOptions {
    readonly availableWeaponIds?: readonly string[];
    readonly consumableFallbackIds?: readonly string[];
    readonly rng?: RandomSource;
    readonly getCurrentHealth?: () => number;
}

export class UpgradeSystem {
    public readonly maxWeapons: number;
    public readonly maxPassives: number;

    private weaponSystem: WeaponSystem;
    private playerStats: PlayerStats;
    private eventBus: EventBus;
    private contentRegistry: ContentRegistry;
    private availableWeapons: readonly string[];
    private consumableFallbackIds: readonly string[];
    private rng: RandomSource;
    private readonly getCurrentHealth?: () => number;
    private acquiredPassives: ActivePassive[] = [];
    private readonly logger = createLogger('UpgradeSystem');

    constructor(
        weaponSystem: WeaponSystem,
        playerStats: PlayerStats,
        contentRegistry: ContentRegistry = loadContentRegistry(),
        rngOrOptions: RandomSource | UpgradeSystemOptions = Math.random
    ) {
        this.weaponSystem = weaponSystem;
        this.playerStats = playerStats;
        this.contentRegistry = contentRegistry;
        const options: UpgradeSystemOptions = typeof rngOrOptions === 'function' ? { rng: rngOrOptions } : rngOrOptions;
        this.rng = options.rng ?? Math.random;
        this.getCurrentHealth = options.getCurrentHealth;
        this.eventBus = EventBus.getInstance();
        const settings = this.contentRegistry.getGameSettings();
        this.maxWeapons = settings.maxWeapons;
        this.maxPassives = settings.maxPassives;
        this.availableWeapons = options.availableWeaponIds ?? settings.availableWeaponIds;
        this.consumableFallbackIds = options.consumableFallbackIds ?? settings.consumableFallbackIds;
        this.logger.debug('constructor:availability-configured', {
            availableWeapons: this.availableWeapons,
            consumableFallbackIds: this.consumableFallbackIds
        });
    }

    public getChestReward(): ChestRewardResult {
        const evolution = this.checkEvolutionAvailability();
        if (evolution) {
            this.logger.info('Chest reward mode selected', { mode: 'evolution', evolvedWeaponId: evolution.evolvedWeaponId });
            return { mode: 'evolution', evolution };
        }

        const roll = this.rng();
        const count = roll < 0.25 ? 3 : 1;
        this.logger.debug('Chest reward count roll resolved', { roll, count, multiRewardChance: 0.25 });

        const upgrades = this.getUpgradeChoices(count);
        const mode = count > 1 ? 'multi-upgrade' : 'single-upgrade';
        this.logger.info('Chest reward mode selected', { mode, count: upgrades.length });
        return { mode, upgrades };
    }

    public checkEvolutionAvailability(): EvolutionRewardPayload | null {
        const activeWeapons = this.weaponSystem.getActiveWeapons();
        const loc = LocalizationManager.getInstance();

        for (const weapon of activeWeapons) {
            this.logger.debug('Checking evolution recipe', { weaponId: weapon.id });
            const recipe = this.contentRegistry.findEvolutionRecipe(weapon.id);
            if (!recipe || activeWeapons.some(item => item.id === recipe.evolvedWeaponId)) continue;

            const def = this.contentRegistry.getWeapon(weapon.id);
            const evolvedDef = this.contentRegistry.getWeapon(recipe.evolvedWeaponId);
            const passive = this.acquiredPassives.find(item => item.id === recipe.requiredPassiveId);

            this.logger.debug('Evolution recipe requirements checked', {
                baseWeaponId: weapon.id,
                weaponLevel: weapon.level,
                requiredPassiveId: recipe.requiredPassiveId,
                passiveLevel: passive?.level,
                requiredPassiveLevel: recipe.requiredPassiveLevel
            });

            if (!evolvedDef) {
                this.logger.error('Missing evolved weapon definition', {
                    baseWeaponId: weapon.id,
                    evolvedWeaponId: recipe.evolvedWeaponId
                });
                continue;
            }

            if (def && weapon.level >= def.levels.length && passive && passive.level >= recipe.requiredPassiveLevel) {
                const nameKey = evolvedDef.nameKey ?? `weapon.${evolvedDef.id}.name`;
                const localizedName = loc.getText(nameKey);
                return {
                    baseWeapon: weapon.id,
                    evolvedWeapon: recipe.evolvedWeaponId,
                    baseWeaponId: weapon.id,
                    evolvedWeaponId: recipe.evolvedWeaponId,
                    name: localizedName !== nameKey ? localizedName : evolvedDef.name,
                    icon: WEAPON_ICONS[evolvedDef.id] ?? evolvedDef.sprite,
                    color: 0xff00ff,
                    nameKey,
                    descriptionKey: evolvedDef.descriptionKey ?? `weapon.${evolvedDef.id}.description.1`
                };
            }
        }

        return null;
    }

    public evolveWeapon(baseWeaponId: string, evolvedWeaponId: string): EvolutionApplyResult {
        this.logger.info('Evolving weapon START', { baseWeaponId, evolvedWeaponId });

        const activeWeapons = this.weaponSystem.getActiveWeapons();
        const baseWeapon = activeWeapons.find(weapon => weapon.id === baseWeaponId);
        if (!baseWeapon) {
            this.logger.warn('Evolution rejected because base weapon is inactive', { baseWeaponId, evolvedWeaponId });
            return { success: false, reason: 'base-weapon-inactive' };
        }

        const evolvedDefinition = this.contentRegistry.getWeapon(evolvedWeaponId);
        if (!evolvedDefinition) {
            this.logger.error('Evolution rejected because evolved weapon definition is missing', { baseWeaponId, evolvedWeaponId });
            return { success: false, reason: 'missing-evolved-definition' };
        }

        if (activeWeapons.some(weapon => weapon.id === evolvedWeaponId)) {
            this.logger.warn('Evolution rejected because evolved weapon is already active', { baseWeaponId, evolvedWeaponId });
            return { success: false, reason: 'evolved-weapon-already-active' };
        }

        const removed = this.weaponSystem.removeWeapon(baseWeaponId);
        if (!removed) {
            this.logger.warn('Evolution failed while removing base weapon', { baseWeaponId, evolvedWeaponId });
            return { success: false, reason: 'remove-base-failed' };
        }

        const unlocked = this.weaponSystem.unlockWeapon(evolvedWeaponId);
        if (!unlocked) {
            this.logger.warn('Evolution failed while unlocking evolved weapon; restoring base weapon', { baseWeaponId, evolvedWeaponId });
            this.weaponSystem.unlockWeapon(baseWeaponId);
            return { success: false, reason: 'unlock-evolved-failed' };
        }

        this.logger.info('Evolving weapon END', { baseWeaponId, evolvedWeaponId });
        return { success: true };
    }

    public getUpgradeChoices(count: number = 3): UpgradeOption[] {
        const activeWeapons = this.weaponSystem.getActiveWeapons();
        const weaponSlotsFull = activeWeapons.length >= this.maxWeapons;
        const passiveSlotsFull = this.acquiredPassives.length >= this.maxPassives;

        const weaponUpgrades = this.buildWeaponLevelOptions(activeWeapons);
        const newWeapons = weaponSlotsFull ? [] : this.buildWeaponUnlockOptions(activeWeapons);
        const passiveUpgrades = this.buildPassiveLevelOptions();
        const newPassives = passiveSlotsFull ? [] : this.buildPassiveUnlockOptions();

        if (weaponSlotsFull) {
            this.logger.info('Weapon slot cap prevents new weapon unlock choices', {
                activeWeapons: activeWeapons.length,
                maxWeapons: this.maxWeapons
            });
        }

        if (passiveSlotsFull) {
            this.logger.info('Passive slot cap prevents new passive unlock choices', {
                activePassives: this.acquiredPassives.length,
                maxPassives: this.maxPassives
            });
        }

        const consumables = this.buildConsumableOptions();
        const maxHealth = this.playerStats.get('maxHealth');
        const missingHealth = maxHealth - (this.getCurrentHealth?.() ?? maxHealth);
        const healing = consumables.filter(option =>
            this.contentRegistry.getConsumable(option.consumableId!)?.type === 'heal' &&
            missingHealth >= option.value!
        );
        const upgradePool = [...weaponUpgrades, ...newWeapons, ...passiveUpgrades, ...newPassives, ...healing];
        this.logger.debug('Generated upgrade pools', {
            requested: count,
            weapons: activeWeapons.length,
            passives: this.acquiredPassives.length,
            weaponUpgrades: weaponUpgrades.length,
            newWeapons: newWeapons.length,
            passiveUpgrades: passiveUpgrades.length,
            newPassives: newPassives.length,
            totalEligible: upgradePool.length
        });

        const choices = this.pickRandom(upgradePool, count);
        if (choices.length < count) {
            this.logger.debug('Using consumable fallback rewards', {
                selectedUpgradeChoices: choices.length,
                requested: count,
                reason: upgradePool.length === 0 ? 'no eligible upgrades' : 'not enough eligible upgrades'
            });
            choices.push(...this.pickRandom(consumables.filter(option => !choices.some(choice => choice.id === option.id)), count - choices.length));
        }

        return choices;
    }

    public applyUpgrade(option: UpgradeOption): boolean {
        if (!option || typeof option !== 'object' || Array.isArray(option)) {
            this.logger.warn('Invalid upgrade option', { reason: 'expected-object' });
            return false;
        }
        this.logger.debug('Applying upgrade START', {
            id: option.id,
            type: option.type,
            weaponId: option.weaponId,
            passiveId: option.passiveId,
            consumableId: option.consumableId
        });

        let accepted = false;
        switch (option.type) {
            case 'weapon_unlock':
                accepted = this.applyWeaponUnlock(option);
                break;
            case 'weapon_level':
                accepted = Boolean(option.weaponId && this.weaponSystem.upgradeWeaponForTesting(option.weaponId).ok);
                break;
            case 'passive_unlock':
                accepted = this.applyPassiveUnlock(option);
                break;
            case 'passive_level':
                accepted = this.applyPassiveLevel(option);
                break;
            case 'consumable':
                accepted = this.applyConsumable(option);
                break;
            default:
                this.logger.warn('Unsupported upgrade type', { type: option.type });
        }

        this.logger.debug('Applying upgrade END', { id: option.id, type: option.type, accepted });
        return accepted;
    }

    public getAcquiredPassives(): ActivePassive[] {
        return this.acquiredPassives.map(passive => ({ ...passive }));
    }

    public getAcquiredPassiveSnapshots(): ActivePassiveSnapshot[] {
        return this.acquiredPassives.flatMap(passive => {
            const def = this.contentRegistry.getPassive(passive.id);
            if (!def) return [];

            return [{
                ...passive,
                nameKey: def.nameKey,
                icon: def.icon,
                color: def.color,
                maxLevel: def.levels.length
            }];
        });
    }

    public getInventorySnapshot(): UpgradeInventorySnapshot {
        return {
            weapons: this.weaponSystem.getActiveWeapons(),
            passives: this.getAcquiredPassiveSnapshots(),
            maxWeapons: this.maxWeapons,
            maxPassives: this.maxPassives
        };
    }

    public grantWeaponForTesting(payload: GiveWeaponDebugPayload): DebugCommandResult<GiveWeaponDebugResult> {
        const source = resolveDebugCommandSource(payload.source);
        this.logger.debug('grantWeaponForTesting:input', {
            payload,
            source,
            inventoryBefore: this.getInventorySnapshot()
        });

        const fallbackSelection = this.resolveDefaultWeaponSelection();
        const requestedWeaponId = payload.weaponId ?? fallbackSelection?.weaponId;
        if (!requestedWeaponId) {
            const reason = this.weaponSystem.getActiveWeapons().length >= this.maxWeapons
                ? 'weapon-slot-full'
                : 'weapon-max-level';
            this.logger.warn('grantWeaponForTesting:no-eligible-weapon', {
                reason,
                maxWeapons: this.maxWeapons,
                activeWeapons: this.weaponSystem.getActiveWeapons().length
            });
            return createDebugCommandFailure('giveWeapon', source, reason, 'No eligible weapon available for debug grant.');
        }

        if (!this.contentRegistry.hasWeapon(requestedWeaponId)) {
            this.logger.warn('grantWeaponForTesting:unknown-weapon', { weaponId: requestedWeaponId });
            return createDebugCommandFailure('giveWeapon', source, 'unknown-weapon', 'Unknown weapon ID.', {
                weaponId: requestedWeaponId
            });
        }

        const weaponDefinition = this.contentRegistry.getWeapon(requestedWeaponId);
        if (!weaponDefinition) {
            return createDebugCommandFailure('giveWeapon', source, 'unknown-weapon', 'Weapon definition is unavailable.', {
                weaponId: requestedWeaponId
            });
        }

        const targetLevel = payload.targetLevel ?? (payload.weaponId ? 1 : fallbackSelection?.targetLevel ?? 1);
        if (!Number.isInteger(targetLevel) || targetLevel < 1) {
            this.logger.warn('grantWeaponForTesting:invalid-target-level', {
                weaponId: requestedWeaponId,
                targetLevel
            });
            return createDebugCommandFailure('giveWeapon', source, 'invalid-payload', 'targetLevel must be a positive integer.', {
                weaponId: requestedWeaponId,
                targetLevel
            });
        }

        const maxLevel = weaponDefinition.levels.length;
        if (targetLevel > maxLevel) {
            this.logger.warn('grantWeaponForTesting:target-level-above-max', {
                weaponId: requestedWeaponId,
                targetLevel,
                maxLevel
            });
            return createDebugCommandFailure('giveWeapon', source, 'weapon-max-level', 'targetLevel is above weapon max level.', {
                weaponId: requestedWeaponId,
                targetLevel,
                maxLevel
            });
        }

        const activeWeaponsBefore = this.weaponSystem.getActiveWeapons();
        const activeWeaponBefore = activeWeaponsBefore.find(weapon => weapon.id === requestedWeaponId);
        const shouldUnlock = !activeWeaponBefore;
        if (shouldUnlock && activeWeaponsBefore.length >= this.maxWeapons) {
            this.logger.warn('grantWeaponForTesting:weapon-slot-full', {
                weaponId: requestedWeaponId,
                activeWeapons: activeWeaponsBefore.length,
                maxWeapons: this.maxWeapons
            });
            return createDebugCommandFailure('giveWeapon', source, 'weapon-slot-full', 'Weapon slots are full.', {
                weaponId: requestedWeaponId,
                maxWeapons: this.maxWeapons
            });
        }

        let unlocked = false;
        if (shouldUnlock) {
            unlocked = this.weaponSystem.unlockWeapon(requestedWeaponId);
            if (!unlocked) {
                this.logger.warn('grantWeaponForTesting:unlock-failed', {
                    weaponId: requestedWeaponId
                });
                return createDebugCommandFailure('giveWeapon', source, 'execution-failed', 'Failed to unlock weapon.', {
                    weaponId: requestedWeaponId
                });
            }
        }

        let currentWeapon = this.weaponSystem.getActiveWeapons().find(weapon => weapon.id === requestedWeaponId);
        if (!currentWeapon) {
            this.logger.error('grantWeaponForTesting:weapon-missing-after-unlock', {
                weaponId: requestedWeaponId
            });
            return createDebugCommandFailure('giveWeapon', source, 'execution-failed', 'Weapon is missing after unlock.', {
                weaponId: requestedWeaponId
            });
        }

        while (currentWeapon.level < targetLevel) {
            const upgradeResult = this.weaponSystem.upgradeWeaponForTesting(requestedWeaponId);
            if (!upgradeResult.ok) {
                this.logger.warn('grantWeaponForTesting:upgrade-rejected', {
                    weaponId: requestedWeaponId,
                    reason: upgradeResult.reason,
                    currentLevel: upgradeResult.currentLevel,
                    targetLevel
                });
                return createDebugCommandFailure(
                    'giveWeapon',
                    source,
                    upgradeResult.reason ?? 'execution-failed',
                    'Failed to upgrade weapon to requested level.',
                    {
                        weaponId: requestedWeaponId,
                        targetLevel,
                        currentLevel: upgradeResult.currentLevel
                    }
                );
            }

            currentWeapon = this.weaponSystem.getActiveWeapons().find(weapon => weapon.id === requestedWeaponId);
            if (!currentWeapon) {
                return createDebugCommandFailure('giveWeapon', source, 'execution-failed', 'Weapon disappeared during upgrade.', {
                    weaponId: requestedWeaponId
                });
            }
        }

        const action: GiveWeaponDebugResult['action'] = unlocked
            ? (currentWeapon.level > 1 ? 'leveled' : 'unlocked')
            : (currentWeapon.level > (activeWeaponBefore?.level ?? currentWeapon.level) ? 'leveled' : 'already-owned');
        const result = createDebugCommandSuccess('giveWeapon', source, {
            weaponId: requestedWeaponId,
            targetLevel,
            currentLevel: currentWeapon.level,
            unlocked,
            action,
            inventory: this.toDebugInventorySnapshot(this.getInventorySnapshot())
        });
        this.logger.info('grantWeaponForTesting:accepted', {
            weaponId: requestedWeaponId,
            targetLevel,
            currentLevel: currentWeapon.level,
            unlocked,
            action
        });
        return result;
    }

    private resolveDefaultWeaponSelection(): { weaponId: string; targetLevel: number } | null {
        const activeWeapons = this.weaponSystem.getActiveWeapons();
        const activeWeaponById = new Map(activeWeapons.map(weapon => [weapon.id, weapon] as const));

        for (const availableWeaponId of this.availableWeapons) {
            const activeWeapon = activeWeaponById.get(availableWeaponId);
            if (!activeWeapon) {
                return { weaponId: availableWeaponId, targetLevel: 1 };
            }
        }

        for (const availableWeaponId of this.availableWeapons) {
            const activeWeapon = activeWeaponById.get(availableWeaponId);
            if (!activeWeapon) {
                continue;
            }

            if (activeWeapon.level < activeWeapon.maxLevel) {
                return { weaponId: availableWeaponId, targetLevel: activeWeapon.level + 1 };
            }
        }

        return null;
    }

    private toDebugInventorySnapshot(inventory: UpgradeInventorySnapshot): DebugInventorySnapshot {
        return {
            activeWeaponCount: inventory.weapons.length,
            maxWeapons: inventory.maxWeapons,
            activePassiveCount: inventory.passives.length,
            maxPassives: inventory.maxPassives,
            weapons: inventory.weapons.map(weapon => ({
                id: weapon.id,
                level: weapon.level,
                maxLevel: weapon.maxLevel
            })),
            passives: inventory.passives.map(passive => ({
                id: passive.id,
                level: passive.level,
                maxLevel: passive.maxLevel
            }))
        };
    }

    private buildWeaponLevelOptions(activeWeapons: ActiveWeaponSnapshot[]): UpgradeOption[] {
        const loc = LocalizationManager.getInstance();

        return activeWeapons.flatMap(weapon => {
            this.logger.debug('Weapon lookup for level option', { weaponId: weapon.id });
            const def = this.contentRegistry.getWeapon(weapon.id);
            if (!def) return [];

            const nextLevel = weapon.level + 1;
            if (nextLevel > def.levels.length) return [];

            const weaponName = loc.getText(`weapon.${weapon.id}.name`) || def.name;
            const descKey = `weapon.${weapon.id}.description.${nextLevel}`;
            const description = loc.getText(descKey);

            return [{
                id: `upgrade_${weapon.id}`,
                type: 'weapon_level' as const,
                title: loc.getText('upgrade.level', [weaponName, nextLevel]),
                description,
                weaponId: weapon.id,
                icon: WEAPON_ICONS[def.id] ?? def.sprite
            }];
        });
    }

    private buildWeaponUnlockOptions(activeWeapons: ActiveWeaponSnapshot[]): UpgradeOption[] {
        const loc = LocalizationManager.getInstance();

        return this.availableWeapons.flatMap(weaponId => {
            if (activeWeapons.some(weapon => weapon.id === weaponId)) return [];

            this.logger.debug('Weapon lookup for unlock option', { weaponId });
            const def = this.contentRegistry.getWeapon(weaponId);
            if (!def) return [];

            const weaponName = loc.getText(`weapon.${weaponId}.name`) || def.name;
            const descKey = `weapon.${weaponId}.description.1`;
            const description = loc.getText(descKey);

            return [{
                id: `unlock_${weaponId}`,
                type: 'weapon_unlock' as const,
                title: loc.getText('upgrade.unlock', [weaponName]),
                description: description !== descKey ? description : loc.getText('upgrade.fallback.desc'),
                weaponId,
                icon: WEAPON_ICONS[def.id] ?? def.sprite
            }];
        });
    }

    private buildPassiveLevelOptions(): UpgradeOption[] {
        return this.acquiredPassives.flatMap(passive => {
            const nextLevel = passive.level + 1;
            const def = this.contentRegistry.getPassive(passive.id);
            const level = this.getPassiveLevel(def, nextLevel);
            if (!level || !def) return [];

            return [this.createPassiveOption('passive_level', passive.id, nextLevel)];
        });
    }

    private buildPassiveUnlockOptions(): UpgradeOption[] {
        return this.getPassiveIds()
            .filter(passiveId => !this.acquiredPassives.some(passive => passive.id === passiveId))
            .map(passiveId => this.createPassiveOption('passive_unlock', passiveId, 1));
    }

    private createPassiveOption(type: 'passive_unlock' | 'passive_level', passiveId: PassiveId, level: number): UpgradeOption {
        const loc = LocalizationManager.getInstance();
        this.logger.debug('Passive lookup for option', { passiveId, level, type });
        const def = this.contentRegistry.getPassive(passiveId);
        if (!def) {
            this.logger.error('Missing passive definition for option', { passiveId, level, type });
            return {
                id: `${type}_${passiveId}`,
                type,
                title: passiveId,
                description: loc.getText('upgrade.fallback.desc'),
                passiveId
            };
        }

        const passiveName = loc.getText(def.nameKey);
        const levelDef = this.getPassiveLevel(def, level);
        const titleKey = type === 'passive_unlock' ? 'upgrade.unlock' : 'upgrade.level';

        return {
            id: `${type === 'passive_unlock' ? 'unlock' : 'upgrade'}_${passiveId}`,
            type,
            title: loc.getText(titleKey, [passiveName, level]),
            description: levelDef ? loc.getText(levelDef.descriptionKey) : loc.getText('upgrade.fallback.desc'),
            passiveId,
            icon: def.icon,
            color: def.color
        };
    }

    private buildConsumableOptions(): UpgradeOption[] {
        const loc = LocalizationManager.getInstance();

        const options = this.consumableFallbackIds.flatMap(consumableId => {
            const consumable = this.contentRegistry.getConsumable(consumableId);
            if (!consumable) {
                this.logger.warn('Skipping missing consumable fallback', { consumableId });
                return [];
            }

            return [{
                id: consumable.id,
                type: 'consumable' as const,
                title: loc.getText(consumable.nameKey),
                description: loc.getText(consumable.descriptionKey),
                consumableId: consumable.id,
                value: consumable.value,
                color: consumable.color,
                icon: consumable.icon
            }];
        });

        this.logger.debug('Consumable fallback pool resolved', {
            fallbackIds: this.consumableFallbackIds,
            options: options.map(option => option.id)
        });

        return options;
    }

    private applyWeaponUnlock(option: UpgradeOption): boolean {
        if (!option.weaponId) return false;

        const activeWeapons = this.weaponSystem.getActiveWeapons();
        if (activeWeapons.length >= this.maxWeapons) {
            this.logger.warn('Weapon unlock rejected because weapon slots are full', {
                weaponId: option.weaponId,
                activeWeapons: activeWeapons.length,
                maxWeapons: this.maxWeapons
            });
            return false;
        }

        return this.weaponSystem.unlockWeapon(option.weaponId);
    }

    private applyPassiveUnlock(option: UpgradeOption): boolean {
        if (!option.passiveId) return false;
        if (!this.getPassiveLevel(this.contentRegistry.getPassive(option.passiveId), 1)) {
            this.logger.warn('Passive unlock rejected because definition is missing', { passiveId: option.passiveId });
            return false;
        }

        if (this.acquiredPassives.length >= this.maxPassives) {
            this.logger.warn('Passive unlock rejected because passive slots are full', {
                passiveId: option.passiveId,
                activePassives: this.acquiredPassives.length,
                maxPassives: this.maxPassives
            });
            return false;
        }

        if (this.acquiredPassives.some(passive => passive.id === option.passiveId)) {
            this.logger.warn('Passive unlock rejected because passive is already owned', { passiveId: option.passiveId });
            return false;
        }

        this.acquiredPassives.push({ id: option.passiveId, level: 1 });
        this.recalculatePassiveStats(option.passiveId);
        this.logger.info('Passive unlocked', {
            passiveId: option.passiveId,
            level: 1,
            affectedStats: this.getPassiveModifierKeys(option.passiveId, 1)
        });
        return true;
    }

    private applyPassiveLevel(option: UpgradeOption): boolean {
        if (!option.passiveId) return false;

        const passive = this.acquiredPassives.find(item => item.id === option.passiveId);
        if (!passive) {
            this.logger.warn('Passive level rejected because passive is not owned', { passiveId: option.passiveId });
            return false;
        }

        const maxLevel = this.getPassiveMaxLevel(option.passiveId);
        if (passive.level >= maxLevel) {
            this.logger.warn('Passive level rejected because passive is maxed', {
                passiveId: option.passiveId,
                level: passive.level,
                maxLevel
            });
            return false;
        }

        passive.level += 1;
        this.recalculatePassiveStats(passive.id);
        this.logger.info('Passive leveled', {
            passiveId: passive.id,
            level: passive.level,
            affectedStats: this.getPassiveModifierKeys(passive.id, passive.level)
        });
        return true;
    }

    private recalculatePassiveStats(passiveId: string): void {
        const previousMaxHealth = this.playerStats.get('maxHealth');
        this.playerStats.recalculate(this.getPassiveStatSnapshots());
        const maxHealth = this.playerStats.get('maxHealth');
        const addedHealth = Math.max(0, maxHealth - previousMaxHealth);
        if (addedHealth > 0) {
            this.eventBus.publish({ type: 'player:heal', data: { amount: addedHealth } });
        }
        this.logger.debug('Passive stats applied', { passiveId, previousMaxHealth, maxHealth, addedHealth });
    }

    private applyConsumable(option: UpgradeOption): boolean {
        if (!option.consumableId) return false;

        const consumable = this.contentRegistry.getConsumable(option.consumableId);
        if (!consumable) {
            this.logger.warn('Unsupported consumable requested', { consumableId: option.consumableId });
            return false;
        }

        const amount = option.value ?? consumable.value;
        if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
            this.logger.warn('Invalid consumable amount', { consumableId: consumable.id, reason: 'expected-finite-positive-number' });
            return false;
        }

        switch (consumable.type) {
            case 'heal':
                this.eventBus.publish({ type: 'player:heal', data: { amount } });
                this.logger.info('Consumable applied', {
                    consumableId: consumable.id,
                    type: consumable.type,
                    amount
                });
                return true;
            case 'gold':
                this.eventBus.publish({ type: 'player:gold', data: { amount } });
                this.logger.info('Consumable applied', {
                    consumableId: consumable.id,
                    type: consumable.type,
                    amount
                });
                return true;
            default:
                this.logger.warn('Unsupported consumable type requested', { consumableId: consumable.id, type: consumable.type });
                return false;
        }
    }

    private pickRandom<T>(pool: T[], count: number): T[] {
        const remaining = [...pool];
        const choices: T[] = [];

        while (choices.length < count && remaining.length > 0) {
            const index = Math.floor(this.rng() * remaining.length);
            const choice = remaining[index];
            if (choice) choices.push(choice);
            remaining.splice(index, 1);
        }

        return choices;
    }

    private getPassiveIds(): string[] {
        return this.contentRegistry.getPassives().map(passive => passive.id);
    }

    private getPassiveLevel(def: PassiveContent | undefined, level: number) {
        return def?.levels.find(passiveLevel => passiveLevel.level === level);
    }

    private getPassiveMaxLevel(passiveId: string): number {
        return this.contentRegistry.getPassive(passiveId)?.levels.length ?? 0;
    }

    private getPassiveStatSnapshots(): Array<ActivePassive & { modifiers?: PassiveModifier }> {
        return this.acquiredPassives.map(passive => ({
            ...passive,
            modifiers: this.getPassiveLevel(this.contentRegistry.getPassive(passive.id), passive.level)?.modifiers
        }));
    }

    private getPassiveModifierKeys(passiveId: string, level: number): string[] {
        const passive = this.contentRegistry.getPassive(passiveId);
        const modifiers = this.getPassiveLevel(passive, level)?.modifiers;
        return modifiers ? Object.keys(modifiers) : [];
    }
}
